import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Typography,
  makeStyles,
  FormHelperText,
} from "@material-ui/core";
import {
  Settings,
  Delete,
  Refresh,
  Close as CloseIcon,
} from "@material-ui/icons";
import { useRouter } from "../util";
import { parseStringParam } from "/lib/util/params";
import useReportConfigData, { timeToCron } from "./useReportConfigData";
import axios from "axios";
import CustomTimePicker from "./TimePicker";

export function isValidEmail(email: string): boolean {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(String(email).toLowerCase());
}

const Timezones = {
  // United States
  US_EASTERN: "America/New_York",
  US_CENTRAL: "America/Chicago",
  US_MOUNTAIN: "America/Denver",
  US_PACIFIC: "America/Los_Angeles",
  US_ALASKA: "America/Anchorage",
  US_HAWAII: "Pacific/Honolulu",
  US_ARIZONA: "America/Phoenix",
  US_INDIANA_EAST: "America/Indiana/Indianapolis",

  // Australia
  AU_EASTERN: "Australia/Sydney",
  AU_CENTRAL: "Australia/Adelaide",
  AU_WESTERN: "Australia/Perth",
  AU_QUEENSLAND: "Australia/Brisbane",
  AU_MELBOURNE: "Australia/Melbourne",

  // New Zealand
  NZ: "Pacific/Auckland",

  // Malaysia
  MY: "Asia/Kuala_Lumpur",

  // UTC
  UTC: "UTC",
};

const timezonesArray = Object.entries(Timezones).map(([key, value]) => ({
  label: `${key.replace(/_/g, " ")} (${value})`,
  value: value,
}));

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  dialogTitle: {
    margin: 0,
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(1),
  },
}));

export default function ReportConfigurationDialog() {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [email, setEmail] = useState("");
  const router = useRouter();
  const namespace = parseStringParam(
    (router.match.params as any).namespace,
    "",
  );
  const {
    reportConfig: reportConfigData,
    loading,
    error,
    refetch,
  } = useReportConfigData(namespace);
  const [subscribedUsers, setSubscribedUsers] = useState<string[]>([]);
  const [timezone, setTimezone] = useState(Timezones.UTC);
  const [emailSentTime, setEmailSentTime] = useState("");
  const [reportSendTime, setReportSendTime] = useState("");
  const [emailError, setEmailError] = useState("");
  const [reportStartTime, setReportStartTime] = useState("");
  const [reportEndTime, setReportEndTime] = useState("");

  useEffect(() => {
    if (reportConfigData) {
      setSubscribedUsers(reportConfigData.subscriber || []);
      setTimezone(reportConfigData.timezone || Timezones.UTC);
      setEmailSentTime(reportConfigData.email_send_at || "");
      setReportSendTime(reportConfigData.report_send_at || "");
      setReportStartTime(reportConfigData.report_start_time || "");
      setReportEndTime(reportConfigData.report_end_time || "");
    }
  }, [reportConfigData]);

  const handleClickOpen = () => {
    setOpen(true);
    setEditMode(false);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleAddSubscription = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const emailsToAdd = email.split(",").map((e) => e.trim());
      const validEmails = emailsToAdd.filter(
        (e) => isValidEmail(e) && !subscribedUsers.includes(e),
      );
      const invalidEmails = emailsToAdd.filter((e) => !isValidEmail(e));
      const duplicateEmails = emailsToAdd.filter((e) =>
        subscribedUsers.includes(e),
      );

      if (validEmails.length > 0) {
        setSubscribedUsers((prevUsers) => [...prevUsers, ...validEmails]);
        setEmail("");
      }

      let errorMessage = "";
      if (invalidEmails.length > 0) {
        errorMessage += `Invalid email(s): ${invalidEmails.join(", ")}. `;
      }
      if (duplicateEmails.length > 0) {
        errorMessage += `Already subscribed: ${duplicateEmails.join(", ")}. `;
      }
      setEmailError(errorMessage || "");
    },
    [email, subscribedUsers],
  );

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(""); // Clear error when user starts typing
  };

  const handleDeleteSubscription = useCallback((userEmail: string) => {
    setSubscribedUsers((prevUsers) =>
      prevUsers.filter((user) => user !== userEmail),
    );
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const payload = {
        timezone,
        report_start_time: reportStartTime,
        report_end_time: reportEndTime,
        email_cron: timeToCron(emailSentTime),
        subscriber: subscribedUsers,
      };

      const response = await axios.post(
        `https://api.onemetrics-report-service.hardware-inno.gcpprodau.tbm.sh/api/v1/report/config/${namespace}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        setEditMode(false);
        refetch();
      } else {
        throw new Error("Failed to save configuration");
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving configuration:", error);
    }
  }, [
    timezone,
    emailSentTime,
    reportSendTime,
    subscribedUsers,
    namespace,
    reportStartTime,
    reportEndTime,
    refetch,
  ]);



  const renderSubscriptionList = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "10px",
          }}
        >
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            marginTop: "10px",
          }}
        >
          <Typography color="error" style={{ textAlign: "center" }}>
            Error loading configuration. Please try again.
          </Typography>
          <Button startIcon={<Refresh />} onClick={refetch} color="primary">
            Retry
          </Button>
        </div>
      );
    }

    if (subscribedUsers.length === 0) {
      return (
        <Typography style={{ marginTop: "10px" }}>
          No subscriptions found.
        </Typography>
      );
    }

    return (
      <List>
        {subscribedUsers.map((user, index) => (
          <ListItem key={index}>
            <ListItemText primary={user} />
            {editMode && (
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteSubscription(user)}
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
    );
  };

  // Modify timezone change handler to enable edit mode
  const handleTimezoneChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setTimezone(e.target.value as string);
    setEditMode(true);
  };

  // Modify time picker handlers
  const handleEmailTimeChange = (newTime: string) => {
    setEmailSentTime(newTime);
    setEditMode(true);
  };

  const handleReportStartTimeChange = (newTime: string) => {
    setReportStartTime(newTime);
    setEditMode(true);
  };

  const handleReportEndTimeChange = (newTime: string) => {
    setReportEndTime(newTime);
    setEditMode(true);
  };

  return (
    <React.Fragment>
      <Button onClick={handleClickOpen} startIcon={<Settings />}>
        Configure Report
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="customized-dialog-title"
      >
        <DialogTitle
          id="customized-dialog-title"
          className={classes.dialogTitle}
        >
          Daily Report Configuration
        </DialogTitle>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText>
            Configure your daily report settings below. Reports will be generated based on your specified reporting period and delivered via email at your chosen time.
          </DialogContentText>
          <FormControl fullWidth margin="dense">
            <InputLabel id="timezone-label">Timezone setting</InputLabel>
            <Select
              labelId="timezone-label"
              value={timezone}
              onChange={handleTimezoneChange}
              label="Timezone"
            >
              {timezonesArray.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select the timezone for report generation and email delivery
            </FormHelperText>
          </FormControl>
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
          }}>
            <CustomTimePicker
              value={reportStartTime}
              onChange={handleReportStartTimeChange}
              label="Report Period Start Time"
              helperText="Start time of the reporting period"
             
            />
            <CustomTimePicker
              value={reportEndTime}
              onChange={handleReportEndTimeChange}
              label="Report Period End Time"
              helperText="End time of the reporting period. Report will be generated after this time."
             
            />
          </div>
          <CustomTimePicker
            value={emailSentTime}
            onChange={handleEmailTimeChange}
            label="Email Delivery Time"
            helperText="Will send a daily automated email with the report at the time set"
          />
          <Typography 
            variant="h6" 
            style={{ 
              marginTop: "20px", 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}
          >
            <span>Email Subscriptions</span>
            {!editMode && (
              <Button
                startIcon={<Settings />}
                onClick={handleEdit}
                size="small"
                color="primary"
              >
                Edit Subscriptions
              </Button>
            )}
          </Typography>
          <DialogContentText>
          The email subscription service will send the latest report from Onewatch Cloud to all subscribed users below per namespace.
          </DialogContentText>
          {editMode && (
            <form
              onSubmit={handleAddSubscription}
              style={{ display: "flex", alignItems: "center", gap: "1rem" }}
            >
              <TextField
                margin="dense"
                id="email"
                label="Email Address(es)"
                type="text"
                fullWidth
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={
                  emailError ||
                  "Enter one or multiple email addresses separated by commas"
                }
                required
                style={{ flexGrow: 1 }}
              />
              <Button type="submit" variant="contained" color="primary">
                Add
              </Button>
            </form>
          )}
          {renderSubscriptionList()}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {editMode && (
            <Button onClick={handleSave} color="primary">
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
